import { useEffect, useState, RefObject } from 'react';
import {
  captureSelectionData,
  type SelectionData,
} from '../utils/captureSelectionData';

interface UseTextSelectionOptions {
  enabled?: boolean;
  // To prevent from unselecting text when we have the PDF loading modal
  locked?: boolean;
  onSelectionChange?: (selection: SelectionData | null) => void;
}

/**
 * Detects and tracks text selection within a container element.
 */
export function useTextSelection(
  containerRef: RefObject<HTMLDivElement>,
  options: UseTextSelectionOptions = {},
) {
  const { enabled = true, locked = false, onSelectionChange } = options;
  const [selection, setSelection] = useState<SelectionData | null>(null);

  useEffect(() => {
    if (!enabled) return;

    function handleMouseDown(event: MouseEvent) {
      const target = event.target as HTMLElement;

      if (target.closest('[data-ai-toolbar]')) {
        return;
      }

      if (locked) {
        return;
      }

      setSelection(null);
      onSelectionChange?.(null);
      window.getSelection()?.removeAllRanges();
    }

    function handleMouseUp() {
      if (locked) {
        return;
      }

      const data = captureSelectionData(containerRef.current);
      setSelection(data);
      onSelectionChange?.(data);
    }

    function handleSelectionChange() {
      if (locked) {
        return;
      }

      // Sync with the browser's actual selection state
      // If there's no valid selection in our container, clear our selection state
      const data = captureSelectionData(containerRef.current);
      if (!data) {
        setSelection(null);
        onSelectionChange?.(null);
      }
    }

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [enabled, locked, containerRef, onSelectionChange]);

  function clearSelection() {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  }

  return {
    selection,
    clearSelection,
    hasSelection: !!selection,
  };
}
